import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { QueryState } from '../components/QueryState'
import { DEV_FLAG_EVENT, isDevPanelOn, setDevPanelOn } from '../dev/devFlag'
import { adminApi } from '../lib/adminApi'

/**
 * dev 패널 — 시스템 상태 + 감사 로그 열람 (기획: docs/10_설계/dev-패널-기획-2026-08-18.md).
 *
 * 이 화면의 용도는 **"고쳤다"와 "나갔다"를 그 자리에서 가르는 것**이다 — 마이그레이션
 * 적용 이력과 테이블 건수가 지금 이 서버의 실측값이다. `DEV_PANEL_PASSWORD` 없는 환경에서는
 * 라우트가 없어 404이고(§15-1), 조회는 `dev:access` 권한 세션만 통과한다.
 *
 * 감사 로그의 before/after는 서버 writeAudit 규약상 개인정보 원문을 담지 않는다 —
 * 그래서 그대로 펼쳐 보여도 된다.
 *
 * **오버레이 토글의 정본이 여기다.** 원클릭 로그인을 없애면서(2026-08-19) 플래그를 켤 곳이
 * 사라졌고, 켜는 자리를 이 화면에 뒀다 — 여기까지 온 사람은 이미 `dev:access`를 가진 세션이다.
 */

const AUDIT_LIMIT = 50

export function AdminDevPage() {
  const status = useQuery({ queryKey: ['dev-status'], queryFn: () => adminApi.devStatus() })
  const audit = useQuery({
    queryKey: ['dev-audit', AUDIT_LIMIT],
    queryFn: () => adminApi.devAudit(AUDIT_LIMIT),
  })
  const [openId, setOpenId] = useState<string | null>(null)

  // 오버레이가 스스로 꺼진 것(FAB의 '패널 끄기')도 이 버튼 문구에 따라와야 한다.
  const [overlayOn, setOverlayOn] = useState(isDevPanelOn)
  useEffect(() => {
    const sync = () => setOverlayOn(isDevPanelOn())
    window.addEventListener(DEV_FLAG_EVENT, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(DEV_FLAG_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])

  const counts = status.data?.counts
  const countRows: { label: string; value: number | undefined }[] = [
    { label: '기업', value: counts?.org },
    { label: '해결카드', value: counts?.solutionCard },
    { label: '시민문제', value: counts?.problemCard },
    { label: '게시글', value: counts?.post },
    { label: '관리자 계정', value: counts?.adminUser },
    { label: '활성 세션', value: counts?.liveSession },
  ]

  return (
    <div>
      <h1 className="page-title">개발자 패널</h1>
      <p className="page-desc">
        개발자 권한 계정만 열 수 있는 내부 화면입니다. 접속과 조회는 감사 기록에 남습니다.
      </p>

      <div className="panel">
        <p>
          dev 오버레이 <b>{overlayOn ? '켜짐' : '꺼짐'}</b>
          <span className="muted small">
            {' '}
            — 이 브라우저에만 적용됩니다. 오버레이가 빌드에 포함된 경우에만 화면에 뜹니다.
          </span>
        </p>
        <button
          type="button"
          className="btn ghost"
          data-testid="dev-overlay-toggle"
          onClick={() => setDevPanelOn(!overlayOn)}
        >
          {overlayOn ? '오버레이 끄기' : '오버레이 켜기'}
        </button>
      </div>

      <h2>시스템 상태</h2>
      <QueryState isPending={status.isPending} error={status.error} isEmpty={false}>
        <div className="panel">
          <p>
            실행 환경 <b>{status.data?.nodeEnv}</b> · 마이그레이션{' '}
            <b>{status.data?.migrations.total}</b>건 적용
          </p>
          <table className="admin-table">
            <thead>
              <tr>
                <th>최근 마이그레이션</th>
                <th>적용 시각</th>
              </tr>
            </thead>
            <tbody>
              {status.data?.migrations.recent.map((m) => (
                <tr key={m.filename}>
                  <td>{m.filename}</td>
                  <td className="muted small">{m.appliedAt.slice(0, 19).replace('T', ' ')}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <table className="admin-table">
            <thead>
              <tr>
                {countRows.map((r) => (
                  <th key={r.label}>{r.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                {countRows.map((r) => (
                  <td key={r.label}>{r.value ?? '—'}</td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </QueryState>

      <h2>감사 로그 (최근 {AUDIT_LIMIT}건)</h2>
      <QueryState
        isPending={audit.isPending}
        error={audit.error}
        isEmpty={(audit.data?.items.length ?? 0) === 0}
      >
        <table className="admin-table">
          <thead>
            <tr>
              <th>시각</th>
              <th>행위자</th>
              <th>액션</th>
              <th>대상</th>
              <th>변경 내용</th>
            </tr>
          </thead>
          <tbody>
            {audit.data?.items.map((a) => (
              <tr key={a.id}>
                <td className="muted small">{a.createdAt.slice(0, 19).replace('T', ' ')}</td>
                <td>
                  {a.actorName ?? '—'}
                  {a.actorLoginId !== null && <div className="muted small">{a.actorLoginId}</div>}
                </td>
                <td>{a.action}</td>
                <td className="muted small">
                  {a.entityType}
                  {a.entityId !== null && <div>{a.entityId.slice(0, 8)}…</div>}
                </td>
                <td>
                  {a.before === null && a.after === null ? (
                    <span className="muted small">—</span>
                  ) : openId === a.id ? (
                    <>
                      <button
                        type="button"
                        className="btn ghost small-btn"
                        onClick={() => setOpenId(null)}
                      >
                        접기
                      </button>
                      <pre className="small" style={{ whiteSpace: 'pre-wrap', margin: '4px 0 0' }}>
                        {JSON.stringify({ before: a.before, after: a.after }, null, 2)}
                      </pre>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="btn ghost small-btn"
                      onClick={() => setOpenId(a.id)}
                    >
                      펼치기
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </QueryState>
    </div>
  )
}
